import React from "react";

import Input from "@/editor/components/common/designSystem/Input";
import DimensionFill from "@/svg/modifiers/direction/dimension-fill";
import DimensionFixed from "@/svg/modifiers/direction/dimension-fixed";
import DimensionHug from "@/svg/modifiers/direction/dimension-hug";
import { Combobox } from "@replo/design-system/components/combobox/Combobox";
import { MenuItem } from "@replo/design-system/components/menu/MenuItem";
import { ChevronDown } from "lucide-react";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";
import { parseUnit } from "replo-runtime/shared/utils/units";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

const DIMENSION_OPTIONS = [
  {
    value: "auto" as const,
    label: "Hug",
    displayValue: "Hug",
    icon: DimensionHug,
  },
  {
    value: "full" as const,
    label: "Fill",
    displayValue: "Fill",
    icon: DimensionFill,
  },
  {
    value: "fixed" as const,
    label: "Fixed",
    displayValue: "Fixed",
    icon: DimensionFixed,
  },
];

interface DimensionSelectorProps {
  value: number | "auto" | "full" | null;
  onSelect: (value: number | "auto" | "full" | null) => void;
  placeholder: string;
}

const DimensionSelector: React.FC<DimensionSelectorProps> = ({
  value,
  onSelect,
  placeholder,
}) => {
  const parseDimensionValue = (inputValue: string): number => {
    const result = parseUnit(
      inputValue,
      { value: 0, unit: "px" },
      "default",
      "px",
    );
    if (typeof result.value === "number") {
      return result.value;
    }
    return Number.parseFloat(result.value);
  };
  const getCurrentMode = (): "auto" | "full" | "fixed" => {
    if (value === "auto" || value === "full") {
      return value;
    }
    return "fixed";
  };

  const [selectedMode, setSelectedMode] = useOverridableState<
    "auto" | "full" | "fixed"
  >(getCurrentMode(), (newMode) =>
    exhaustiveSwitch({ type: newMode })({
      auto: () => onSelect("auto"),
      full: () => onSelect("full"),
      fixed: () => onSelect(parseDimensionValue(customValue)),
    }),
  );

  const [customValue, setCustomValue] = useOverridableState<string>(
    typeof value === "number" ? value.toString() : "100",
    (newValue) =>
      exhaustiveSwitch({ type: selectedMode })({
        fixed: () => onSelect(parseDimensionValue(newValue)),
        auto: undefined,
        full: undefined,
      }),
  );

  const dimensionOptions = DIMENSION_OPTIONS.map((option) => {
    const IconComponent = option.icon;
    return {
      value: option.value.toString(),
      displayValue: option.displayValue,
      label: (
        <MenuItem
          variant="default"
          size="sm"
          selected={selectedMode === option.value}
          startEnhancer={<IconComponent className="text-muted" />}
        >
          {option.label}
        </MenuItem>
      ),
    };
  });

  const getDisplayValue = () => {
    if (value == null) {
      return placeholder;
    }
    return exhaustiveSwitch({ type: selectedMode })({
      auto: "Hug",
      full: "Fill",
      fixed: `${customValue}px`,
    });
  };

  const getSelectedIcon = () =>
    exhaustiveSwitch({ type: selectedMode })({
      auto: <DimensionHug className="text-muted" />,
      full: <DimensionFill className="text-muted" />,
      fixed: <DimensionFixed className="text-muted" />,
    });

  return (
    <div className="w-full overflow-hidden">
      {selectedMode === "fixed" ? (
        <div className="flex gap-2 items-center">
          <div className="flex-1">
            <Combobox.Root
              options={dimensionOptions}
              value={selectedMode}
              onChange={(newValue) =>
                setSelectedMode(newValue as "auto" | "full" | "fixed")
              }
            >
              <Combobox.Trigger>
                <Combobox.SelectionButton
                  layoutClassName="w-full"
                  title="Fixed"
                  size="sm"
                  startEnhancer={<DimensionFixed className="text-muted" />}
                  titleAlignment="start"
                  endEnhancer={<ChevronDown size={12} className="text-muted" />}
                />
              </Combobox.Trigger>
              <Combobox.Popover align="center" layoutClassName="w-[140px]">
                <Combobox.Content
                  areOptionsSearchable={false}
                  emptySearchMessage="No matches found"
                />
              </Combobox.Popover>
            </Combobox.Root>
          </div>
          <div className="w-20">
            <Input
              value={customValue}
              onChange={(e) => setCustomValue(e.target.value)}
              placeholder="100"
              size="sm"
              type="number"
              endEnhancer={() => <span className="text-muted text-xs">px</span>}
            />
          </div>
        </div>
      ) : (
        <Combobox.Root
          options={dimensionOptions}
          value={selectedMode}
          onChange={(newValue) =>
            setSelectedMode(newValue as "auto" | "full" | "fixed")
          }
        >
          <Combobox.Trigger>
            <Combobox.SelectionButton
              layoutClassName="w-full"
              title={getDisplayValue()}
              isPlaceholder={!value}
              size="sm"
              startEnhancer={getSelectedIcon()}
              titleAlignment="start"
              endEnhancer={<ChevronDown size={12} className="text-muted" />}
            />
          </Combobox.Trigger>
          <Combobox.Popover align="center" layoutClassName="w-[140px]">
            <Combobox.Content
              areOptionsSearchable={false}
              emptySearchMessage="No matches found"
            />
          </Combobox.Popover>
        </Combobox.Root>
      )}
    </div>
  );
};

export default DimensionSelector;
